import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import VigiaMercado from './VigiaMercado'
import { C } from './theme'
import { RefreshCw, Plus, ChevronDown, ChevronRight } from 'lucide-react'
import { CopyButton } from './CopyButton'

// MERCADO — o relatorio da vigia e as ideias, dentro do site.
//
// O Marco pediu a 17/09/2026 um sitio para todas as ideias, estrategias e
// sugestoes, e quis que ficasse no LogicPro e nao num documento a parte. Este
// ecra tem duas metades:
//
//   1. O RELATORIO do artigo vigiado (relatorios_vigia): o que a funcao
//      relatorio-vigia escreveu na ultima corrida — ritmo, cobertura, lucro por
//      pais, conselhos, e o que lhe faltou. Nada aqui e calculado no browser:
//      mostra-se o que a funcao guardou, para o ecra e o email dizerem o mesmo.
//   2. As IDEIAS (ideias): por artigo ou gerais, com estado e notas. Eu escrevo
//      pela base; o Marco muda o estado e escreve as notas aqui.
//
// Comeca com o Lost Cherry. Quando a vigia alargar a mais artigos, o selector
// em cima passa a ter mais de um.

const fmt = (n, casas = 2) => (n == null || !Number.isFinite(Number(n))) ? '—'
  : Number(n).toLocaleString('pt-PT', { minimumFractionDigits: casas, maximumFractionDigits: casas })
const dataCurta = (iso) => iso ? new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

const ESTADOS = [
  { id: 'nova',       label: 'Nova',        cor: '#2563eb', bg: '#eff6ff' },
  { id: 'em_analise', label: 'Em análise',  cor: '#d97706', bg: '#fffbeb' },
  { id: 'aprovada',   label: 'Aprovada',    cor: '#16a34a', bg: '#f0fdf4' },
  { id: 'feita',      label: 'Feita',       cor: '#52525b', bg: '#f4f4f5' },
  { id: 'descartada', label: 'Descartada',  cor: '#dc2626', bg: '#fef2f2' },
]
const estadoInfo = (id) => ESTADOS.find(e => e.id === id) || ESTADOS[0]
const AREAS = [
  { id: 'lost-cherry', label: 'Lost Cherry e B2C' },
  { id: 'b2b',         label: 'B2B e clientes' },
  { id: 'tesouraria',  label: 'Tesouraria e IVA' },
  { id: 'operacao',    label: 'Operação e sistema' },
  { id: 'estrategia',  label: 'Estratégia 6–12 meses' },
  { id: 'risco',       label: 'Riscos' },
  { id: 'decisao',     label: 'Decisões tomadas e descartadas' },
]
const NIVEL = { alerta: { cor: '#a4262c', label: 'ALERTA' }, atencao: { cor: '#c07400', label: 'ATENÇÃO' }, nota: { cor: C.textMuted, label: 'nota' } }

export default function Mercado() {
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [relatorios, setRelatorios] = useState([])
  const [ideias, setIdeias] = useState([])
  const [ean, setEan] = useState('')
  const [relId, setRelId] = useState(null)
  const [aberta, setAberta] = useState(() => new Set(['lost-cherry']))
  const [mostrarFechadas, setMostrarFechadas] = useState(false)
  const [nova, setNova] = useState(null)
  const [guardando, setGuardando] = useState(false)

  const carregar = async () => {
    setLoading(true); setErro('')
    try {
      const [r, i] = await Promise.all([
        // Sem o HTML: e o email inteiro por linha, e aqui so se usa o resumo.
        supabase.from('relatorios_vigia').select('id, gerado_em, ean, resumo').order('gerado_em', { ascending: false }).limit(60),
        supabase.from('ideias').select('*').order('criado_em', { ascending: false }),
      ])
      if (r.error) throw r.error
      if (i.error) throw i.error
      setRelatorios(r.data || [])
      setIdeias(i.data || [])
      if (!ean && r.data?.length) setEan(r.data[0].ean)
    } catch (e) {
      setErro('Não consegui ler o Mercado: ' + (e?.message || String(e)))
    } finally { setLoading(false) }
  }
  useEffect(() => { carregar() }, [])

  const artigos = useMemo(() => {
    const m = new Map()
    for (const r of relatorios) if (!m.has(r.ean)) m.set(r.ean, r.resumo?.nome || r.ean)
    return [...m.entries()]
  }, [relatorios])
  const doArtigo = useMemo(() => relatorios.filter(r => r.ean === ean), [relatorios, ean])
  const rel = useMemo(() => doArtigo.find(r => r.id === relId) || doArtigo[0] || null, [doArtigo, relId])
  const R = rel?.resumo

  const guardarIdeia = async (id, campos) => {
    setGuardando(true)
    const { error } = await supabase.from('ideias').update(campos).eq('id', id)
    setGuardando(false)
    if (error) { setErro('Não guardou: ' + error.message); return }
    setIdeias(prev => prev.map(x => x.id === id ? { ...x, ...campos } : x))
  }
  const criarIdeia = async () => {
    if (!nova?.ideia?.trim()) return
    setGuardando(true)
    const { data, error } = await supabase.from('ideias').insert({
      ideia: nova.ideia.trim(), justificacao: nova.justificacao || null, custo: nova.custo || null,
      area: nova.area || 'lost-cherry', ean: nova.area === 'lost-cherry' ? (ean || null) : null, autor: 'marco',
    }).select().single()
    setGuardando(false)
    if (error) { setErro('Não criou: ' + error.message); return }
    setIdeias(prev => [data, ...prev]); setNova(null)
  }

  const porArea = useMemo(() => {
    const m = new Map(AREAS.map(a => [a.id, []]))
    for (const i of ideias) {
      const fechada = i.estado === 'feita' || i.estado === 'descartada'
      // As decisoes sao por natureza "fechadas": mostram-se sempre.
      if (fechada && !mostrarFechadas && i.area !== 'decisao') continue
      ;(m.get(i.area) || m.get('operacao')).push(i)
    }
    return m
  }, [ideias, mostrarFechadas])

  // ── PRECOS DE MERCADO POR CLIENTE ──────────────────────────────────────
  // Hoje so o Ruben tem lista, mas o ecra nao o sabe: escolhe-se o cliente numa
  // caixa. No dia em que entrar o segundo, nao ha codigo a mexer.
  const [clientes, setClientes] = React.useState([])
  const [clienteVigia, setClienteVigia] = React.useState('')
  React.useEffect(() => {
    ;(async () => {
      const { data } = await supabase.from('perfis').select('id, nome, email')
        .eq('role', 'cliente').order('nome')
      setClientes(data || [])
      // Abre no cliente que ja tem artigos em analise, para nao obrigar a
      // procurar quem e — na pratica ha sempre um so.
      const { data: comLista } = await supabase.from('vigia_mercado')
        .select('cliente_id').neq('estado', 'removido').limit(1)
      if (comLista?.[0]) setClienteVigia(comLista[0].cliente_id)
    })()
  }, [])

  const toggle = (a) => setAberta(prev => { const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a); return n })

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>A carregar o Mercado…</div>

  return (
    // O <main> tem overflow:hidden, por isso cada pagina trata do seu proprio
    // scroll. Esta nao tratava: tudo o que ficasse abaixo do fundo do ecra era
    // inalcancavel, e ate a ultima linha do relatorio aparecia cortada ao meio.
    // Passou despercebido enquanto nao houve nada por baixo do relatorio.
    <div style={{ padding: '20px 20px 60px', maxWidth: '1100px', margin: '0 auto', flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: C.text }}>Mercado</h2>
          <div style={{ fontSize: '12px', color: C.textMuted }}>O que a vigia do Kaufland viu, o que aconselha, e as ideias em cima da mesa.</div>
        </div>
        <button onClick={carregar} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, cursor: 'pointer', fontSize: 12 }}>
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>
      {erro && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: C.danger, padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{erro}</div>}

      {/* ── O RELATÓRIO ─────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {artigos.length > 1 ? (
            <select value={ean} onChange={e => { setEan(e.target.value); setRelId(null) }} style={{ fontSize: 14, fontWeight: 700, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
              {artigos.map(([e, n]) => <option key={e} value={e}>{n}</option>)}
            </select>
          ) : <strong style={{ fontSize: 15 }}>{R?.nome || 'Sem relatórios ainda'}</strong>}
          {ean && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'monospace', fontSize: 12, color: C.textMuted }}>
              EAN {ean} <CopyButton text={ean} size={12} title="Copiar EAN" />
            </span>
          )}
          {doArtigo.length > 0 && (
            <select value={rel?.id || ''} onChange={e => setRelId(Number(e.target.value))} style={{ fontSize: 12, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted }}>
              {doArtigo.map(r => <option key={r.id} value={r.id}>relatório de {dataCurta(r.gerado_em)}</option>)}
            </select>
          )}
          {R && <span style={{ fontSize: 11, color: C.textFaint }}>janela {dataCurta(R.janela?.de)} → {dataCurta(R.janela?.ate)} · {R.janela?.leituras} leituras</span>}
        </div>

        {!R ? (
          <div style={{ fontSize: 13, color: C.textMuted }}>O primeiro relatório sai na próxima corrida (segunda, quarta e sexta às 09:10).</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
              {[
                ['Stock', `${R.stock ?? '—'}${R.transito > 0 ? ` +${R.transito} a caminho` : ''}`],
                ['Vendidas na janela', `${R.vendidas} · ${fmt(R.ritmo_dia)}/dia`],
                ['Sem venda há', `${Math.floor(R.dias_sem_venda ?? 0)} d`],
                ['Cobertura', R.cobertura_dias == null ? '—' : `${R.cobertura_dias} d`],
                ['Lucro médio/un', R.lucro_medio == null ? '—' : `${fmt(R.lucro_medio)} €`],
              ].map(([k, v]) => (
                <div key={k} style={{ background: C.bg, borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.4px' }}>{k}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Conselhos</div>
            {(R.conselhos || []).length === 0 ? <div style={{ fontSize: 12, color: C.textMuted }}>Nada a assinalar.</div> : (
              <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, lineHeight: 1.5, color: C.text }}>
                {R.conselhos.map((c, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: NIVEL[c.nivel]?.cor || C.textMuted, textTransform: 'uppercase', marginRight: 6 }}>{NIVEL[c.nivel]?.label || c.nivel}</span>{c.texto}
                  </li>
                ))}
              </ul>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead><tr style={{ background: C.bg }}>
                  {['País', 'Preço', 'Stock', 'Lucro/un', 'Buy Box'].map((h, i) => <th key={h} style={{ padding: '6px 8px', textAlign: i === 1 || i === 2 || i === 3 ? 'right' : 'left', fontSize: 10, color: C.textMuted, textTransform: 'uppercase' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(R.paises || []).map(p => (
                    <tr key={p.sf} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700 }}>
                        {p.nome}{p.fechado && <span style={{ fontSize: 10, color: C.textFaint, fontWeight: 400, marginLeft: 6 }}>fechado</span>}
                        {p.idealo && <a href={p.idealo} target="_blank" rel="noreferrer" style={{ fontSize: 10, fontWeight: 400, color: '#2563eb', marginLeft: 6 }}>idealo ↗</a>}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{p.precoLocal}{p.precoEur != null && !String(p.precoLocal).endsWith('€') && <div style={{ fontSize: 10, color: C.textMuted }}>≈ {fmt(p.precoEur)} €</div>}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800 }}>{p.stock ?? '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: p.lucro == null ? C.textMuted : p.lucro < 0 ? C.danger : p.meu ? C.success : C.textMuted }} title={p.meu ? '' : 'o que ganharias ao preço dele'}>
                        {p.lucro == null ? '—' : `${p.meu ? '' : 'a esse preço '}${fmt(p.lucro)} €`}
                      </td>
                      <td style={{ padding: '6px 8px', color: p.meu ? C.success : C.danger }}>
                        {p.meu ? 'tu' : (p.vendedor || '?')}{!p.meu && p.armazem && <span style={{ color: C.textMuted, fontSize: 11 }}> {p.armazem}</span>}
                        {p.ritmoConc != null && <div style={{ fontSize: 10.5, color: C.textMuted }}>{fmt(p.ritmoConc, 1)}/dia{p.esgotaEm ? ` · esgota ~${new Date(p.esgotaEm).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}` : ''}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(R.faltas || []).length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: C.textMuted }}>
                <strong style={{ fontSize: 11 }}>O que faltou para ser mais preciso:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>{R.faltas.map((f, i) => <li key={i}>{f}</li>)}</ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── PREÇOS DE MERCADO DO CLIENTE ────────────────────────────────── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text }}>Preços de mercado do cliente</h3>
            <div style={{ fontSize: 11.5, color: C.textMuted }}>
              Amazon ES e Idealo ES escritos à mão. Escreve, guarda, e carrega em publicar quando estiver tudo — só aí é que o cliente vê e é avisado.
            </div>
          </div>
          <select value={clienteVigia} onChange={e => setClienteVigia(e.target.value)}
            style={{ fontSize: 13, padding: '5px 9px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
            <option value="">— escolhe o cliente —</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome || c.email}</option>)}
          </select>
        </div>
        {clienteVigia
          ? <VigiaMercado modo="admin" clienteId={clienteVigia} />
          : <div style={{ fontSize: 12.5, color: C.textMuted }}>Escolhe um cliente para ver e preencher a lista dele.</div>}
      </div>

      {/* ── AS IDEIAS ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.text }}>Ideias</h3>
          <div style={{ fontSize: 11.5, color: C.textMuted }}>Nada disto se faz sem dizeres "avança". Muda o estado, escreve notas; as decisões ficam registadas com o porquê.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 11.5, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={mostrarFechadas} onChange={e => setMostrarFechadas(e.target.checked)} /> mostrar feitas e descartadas
          </label>
          <button onClick={() => setNova({ area: 'lost-cherry', ideia: '', justificacao: '', custo: '' })} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', border: 'none', borderRadius: 6, background: C.primary, color: '#fff', cursor: 'pointer', fontSize: 12 }}>
            <Plus size={13} /> Nova ideia
          </button>
        </div>
      </div>

      {nova && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 12, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={nova.area} onChange={e => setNova({ ...nova, area: e.target.value })} style={{ padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
              {AREAS.filter(a => a.id !== 'decisao').map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <input value={nova.ideia} onChange={e => setNova({ ...nova, ideia: e.target.value })} placeholder="A ideia, numa frase" style={{ flex: 1, minWidth: 260, padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 }} />
          </div>
          <input value={nova.justificacao} onChange={e => setNova({ ...nova, justificacao: e.target.value })} placeholder="O número que a justifica (opcional)" style={{ padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={nova.custo} onChange={e => setNova({ ...nova, custo: e.target.value })} placeholder="O que custa (opcional)" style={{ flex: 1, padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }} />
            <button onClick={criarIdeia} disabled={guardando || !nova.ideia.trim()} style={{ padding: '6px 12px', border: 'none', borderRadius: 6, background: C.primary, color: '#fff', cursor: 'pointer', fontSize: 12 }}>Guardar</button>
            <button onClick={() => setNova(null)} style={{ padding: '6px 12px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
          </div>
        </div>
      )}

      {AREAS.map(a => {
        const lista = porArea.get(a.id) || []
        const abertaA = aberta.has(a.id)
        return (
          <div key={a.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8 }}>
            <button onClick={() => toggle(a.id)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
              {abertaA ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <strong style={{ fontSize: 13.5, color: C.text }}>{a.label}</strong>
              <span style={{ fontSize: 11, color: C.textMuted }}>{lista.length}</span>
            </button>
            {abertaA && (
              <div style={{ padding: '0 14px 12px' }}>
                {lista.length === 0 && <div style={{ fontSize: 12, color: C.textMuted }}>Nada aqui.</div>}
                {lista.map(i => {
                  const est = estadoInfo(i.estado)
                  return (
                    <div key={i.id} style={{ borderTop: `1px solid ${C.border}`, padding: '10px 0', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px', gap: '6px 14px', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{i.ideia}</div>
                        {i.justificacao && <div style={{ fontSize: 12, color: C.text, marginTop: 2, lineHeight: 1.45 }}>{i.justificacao}</div>}
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>
                          {i.custo && <span>Custa: {i.custo} · </span>}
                          {i.decidido_em && <span>decidido a {new Date(i.decidido_em).toLocaleDateString('pt-PT')} · </span>}
                          {i.porque && <span>{i.porque} · </span>}
                          <span>{i.autor === 'marco' ? 'Marco' : 'Claude'} · {new Date(i.criado_em).toLocaleDateString('pt-PT')}</span>
                        </div>
                        <textarea defaultValue={i.notas || ''} placeholder="Notas tuas…" rows={1}
                          onBlur={e => { const v = e.target.value.trim() || null; if (v !== (i.notas || null)) guardarIdeia(i.id, { notas: v }) }}
                          style={{ width: '100%', marginTop: 6, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      </div>
                      <select value={i.estado} onChange={e => {
                        const estado = e.target.value
                        const fecha = ['aprovada', 'feita', 'descartada'].includes(estado)
                        guardarIdeia(i.id, { estado, decidido_em: fecha ? new Date().toISOString().slice(0, 10) : i.decidido_em })
                      }} style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${est.cor}55`, background: est.bg, color: est.cor, fontSize: 12, fontWeight: 700 }}>
                        {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                      </select>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
